/**
 * Pending quiz-attempt claims.
 *
 * An anonymous quiz attempt is only linked to a user — and its lead follow-ups only
 * scheduled — when the backend `claim()` runs. The card claims directly when a
 * player signs in on it, but a visitor who leaves mid-quiz and signs up later on
 * another surface would otherwise leave the attempt orphaned and the lead lost.
 *
 * The card records an anonymous attempt id here the moment the quiz starts, so even
 * an unfinished attempt is captured. `QuizAttemptClaimer` (mounted app-wide) drains
 * the list on the next authenticated page load anywhere in the app.
 *
 * The store keeps at most ONE entry per quiz, so replaying before signing up claims
 * the last attempt rather than every one. Dedup rule: a completion always replaces
 * the stored entry, and a fresh in-progress start never downgrades an already-
 * completed one. Different quizzes each keep their own entry.
 *
 * All access is best-effort and swallows storage errors (private mode, quota,
 * disabled storage).
 */

const PENDING_KEY = 'pending-quiz-claims';

export interface PendingQuizClaim {
  quizId: string;
  attemptId: string;
  completed: boolean;
}

/** Stored shape: keyed by quizId so there is at most one pending attempt per quiz. */
type PendingMap = Record<string, { attemptId: string; completed: boolean }>;

function readMap(): PendingMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // Ignore anything that isn't the current object shape (e.g. a legacy value).
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: PendingMap = {};
    for (const [quizId, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (
        value &&
        typeof value === 'object' &&
        typeof (value as { attemptId?: unknown }).attemptId === 'string'
      ) {
        const v = value as { attemptId: string; completed?: unknown };
        out[quizId] = { attemptId: v.attemptId, completed: v.completed === true };
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: PendingMap): void {
  if (typeof window === 'undefined') return;
  try {
    if (Object.keys(map).length === 0) {
      window.localStorage.removeItem(PENDING_KEY);
    } else {
      window.localStorage.setItem(PENDING_KEY, JSON.stringify(map));
    }
  } catch {
    // ignore (private mode, full storage, etc.)
  }
}

export function readPendingQuizClaims(): PendingQuizClaim[] {
  return Object.entries(readMap()).map(([quizId, v]) => ({
    quizId,
    attemptId: v.attemptId,
    completed: v.completed,
  }));
}

/**
 * Record an attempt the moment the quiz starts. Never downgrades an already-completed
 * entry for the same quiz — a finished result is worth more than a fresh in-progress
 * one, and if this new attempt is finished `recordQuizAttemptCompleted` takes over.
 */
export function recordQuizAttemptStarted(quizId: string, attemptId: string): void {
  const map = readMap();
  if (map[quizId]?.completed) return;
  map[quizId] = { attemptId, completed: false };
  writeMap(map);
}

/** Record a finished attempt — always wins over an in-progress entry, so the last completed attempt is the one claimed. */
export function recordQuizAttemptCompleted(quizId: string, attemptId: string): void {
  const map = readMap();
  map[quizId] = { attemptId, completed: true };
  writeMap(map);
}

/** Drop the pending entry for a quiz once it has been claimed (or is no longer claimable). */
export function clearPendingQuizClaim(quizId: string): void {
  const map = readMap();
  if (!(quizId in map)) return;
  delete map[quizId];
  writeMap(map);
}
